import React from "react";
import type { Order, OrderStatus } from "@/types";
import { useI18n } from "@/i18n";
import { formatUzs } from "@/lib/format";

/** Price in display serif with a small "UZS" — editorial price setting. */
export function PriceTag({ uzs, className }: { uzs: number; className?: string }) {
  const { locale } = useI18n();
  const amount = formatUzs(uzs, locale).replace(/\s*UZS$/, "");
  return (
    <span className={`price price-tag ${className ?? ""}`}>
      {amount}
      <small>UZS</small>
    </span>
  );
}

/** "◆ Title ◆" divider in the brand ornament. */
export function Ornament({ title }: { title: string }) {
  return (
    <div className="ornament" role="presentation">
      <span />
      <i />
      <b>{title}</b>
      <i />
      <span />
    </div>
  );
}

const METHOD: Record<string, string> = { payme: "Payme", click: "Click", uzum: "Uzum" };
const time = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tashkent" }).format(new Date(iso));
const dayTime = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tashkent" }).format(new Date(iso));

/**
 * "Путь заказа": the real statuses as a timeline with the time each one was
 * reached (from the order's status history). Pickup and delivery have
 * their own fourth step; a cancelled order shows where it stopped.
 */
export function OrderJourney({ order }: { order: Order }) {
  const { t, locale } = useI18n();
  const hist = order.statusHistory ?? [];
  const firstAt = (s: OrderStatus) => hist.find((h) => h.status === s)?.createdAt;
  const pickup = order.deliveryMethod === "pickup";
  const steps: Array<{ key: string; statuses: OrderStatus[]; label: string; hint?: string }> = [
    { key: "new", statuses: ["new", "confirmed"], label: t("journey.placed") },
    {
      key: "paid",
      statuses: ["paid"],
      label: order.paymentMethod === "cash_on_pickup" && order.paymentStatus !== "paid" ? t("journey.payOnPickup") : t("journey.paid"),
    },
    { key: "preparing", statuses: ["preparing"], label: t("journey.preparing") },
    pickup
      ? { key: "ready", statuses: ["ready_for_pickup"], label: t("journey.ready"), hint: t("journey.readyHint") }
      : { key: "courier", statuses: ["handed_to_courier"], label: t("journey.courier"), hint: t("journey.courierHint") },
    { key: "done", statuses: ["delivered"], label: t("journey.received") },
  ];
  const ORDER: OrderStatus[] = ["new", "confirmed", "paid", "preparing", pickup ? "ready_for_pickup" : "handed_to_courier", "delivered"];
  const rank = (s: OrderStatus) => ORDER.indexOf(s);
  const current = order.status === "cancelled" ? -1 : rank(order.status);
  const paidDone = order.paymentStatus === "paid";

  const stepState = (i: number) => {
    const s = steps[i];
    if (s.key === "paid") {
      if (paidDone) return "done";
      return order.paymentMethod === "cash_on_pickup" ? "info" : current >= 0 ? "now" : "todo";
    }
    const stepRank = Math.max(...s.statuses.map(rank));
    if (current > stepRank || (s.key === "done" && current === stepRank)) return "done";
    if (current === stepRank || (s.key === "new" && current <= 1 && !paidDone && order.paymentMethod === "cash_on_pickup")) return "now";
    return "todo";
  };
  const whenOf = (i: number) => {
    const s = steps[i];
    if (s.key === "paid") return firstAt("paid");
    if (s.key === "new") return order.createdAt;
    return s.statuses.map(firstAt).find(Boolean);
  };

  if (order.status === "cancelled") {
    const at = firstAt("cancelled");
    return (
      <div className="journey journey--cancelled">
        <div className="journey__step" data-state="done">
          <b>{t("journey.placed")}</b>
          <small>{dayTime(order.createdAt, locale)}</small>
        </div>
        <div className="journey__step" data-state="cancelled">
          <b>{t("journey.cancelled")}</b>
          <small>{at ? dayTime(at, locale) : ""}</small>
        </div>
      </div>
    );
  }

  return (
    <div className="journey">
      {steps.map((s, i) => {
        const st = stepState(i);
        const at = whenOf(i);
        return (
          <div key={s.key} className="journey__step" data-state={st}>
            <b>{s.label}</b>
            <small>
              {at && st !== "todo" ? (i === 0 ? dayTime(at, locale) : time(at, locale)) : st === "now" && s.hint ? s.hint : ""}
              {i === 1 && paidDone && order.paymentMethod !== "cash_on_pickup" ? ` · ${METHOD[order.paymentMethod] ?? ""}` : ""}
            </small>
          </div>
        );
      })}
    </div>
  );
}

/** Order receipt styled as a pack label: perforated edges, logo, dotted leaders. */
export function ReceiptLabel({ order }: { order: Order }) {
  const { t, locale } = useI18n();
  const rows: Array<[string, number]> = order.items.map((i) => [
    `${i.productName}${i.variantLabel !== "Стандарт" ? ` · ${i.variantLabel}` : ""}${i.quantity > 1 ? ` × ${i.quantity}` : ""}`,
    i.priceUzs * i.quantity,
  ]);
  if (order.deliveryFeeUzs > 0) rows.push([t("checkout.deliveryLine"), order.deliveryFeeUzs]);
  return (
    <div className="receipt">
      <div className="receipt__logo">
        <img src="/brand/kms-logo.png" alt="KMS ROASTING" />
      </div>
      <div className="receipt__no">№ {order.number}</div>
      <div className="receipt__date">{dayTime(order.createdAt, locale)}</div>
      <div className="receipt__cut" />
      {rows.map(([label, sum], i) => (
        <div key={i} className="receipt__line">
          <span>{label}</span>
          <span className="receipt__dots" />
          <span>{formatUzs(sum, locale).replace(/\s*UZS$/, "")}</span>
        </div>
      ))}
      {order.discountUzs > 0 && (
        <div className="receipt__line receipt__line--minus">
          <span>{t("checkout.discountLine", { code: order.promoCode ?? "" })}</span>
          <span className="receipt__dots" />
          <span>−{formatUzs(order.discountUzs, locale).replace(/\s*UZS$/, "")}</span>
        </div>
      )}
      {(order.giftCardUzs ?? 0) > 0 && (
        <div className="receipt__line receipt__line--minus">
          <span>{t("cert.lineShort")}</span>
          <span className="receipt__dots" />
          <span>−{formatUzs(order.giftCardUzs ?? 0, locale).replace(/\s*UZS$/, "")}</span>
        </div>
      )}
      <div className="receipt__cut" />
      <div className="receipt__total">
        <small>{t("cart.total")}</small>
        <span>{formatUzs(order.totalUzs, locale)}</span>
      </div>
      <div className="receipt__dia" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      <div className="receipt__thanks">{t("journey.thanks")}</div>
    </div>
  );
}
